import { useState, type FormEvent } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";

const FORM_URL = "https://readdy.ai/api/form/db6f3622sgkaik1unefg";

const STATS = [
  { value: "20,000+", label: "影视资源", icon: "ri-film-line" },
  { value: "300+", label: "每日更新", icon: "ri-refresh-line" },
  { value: "50万+", label: "注册影迷", icon: "ri-group-line" },
  { value: "12年", label: "用心运营", icon: "ri-award-line" },
];

const SERVICES = [
  {
    icon: "ri-movie-2-line",
    title: "影视浏览与检索",
    desc: "涵盖电影、电视剧多类型分类，支持按类型、地区、年份筛选与关键词搜索，快速定位想看的内容。",
  },
  {
    icon: "ri-newspaper-line",
    title: "影视资讯与解读",
    desc: "提供最新影视新闻、专题报道与深度影评，帮助影迷及时掌握行业动态与作品背后的故事。",
  },
  {
    icon: "ri-heart-3-line",
    title: "个性化收藏",
    desc: "收藏感兴趣的影视内容，记录你的观影清单，随时回顾与分享。",
  },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useSeo({
    title: "关于我们 - 飘花电影网",
    description:
      "了解飘花电影网的网站定位与服务内容。飘花电影网专注影视内容浏览与资讯分享，欢迎通过联系表单与我们沟通反馈。",
    keywords: "关于飘花电影网,网站介绍,联系方式,影视网站",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => body.append(key, String(value)));

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSuccess = response.ok && parsed?.code === "OK";
      const isSpam = String(serverMsg ?? "").toLowerCase().includes("spam");

      if (!isSuccess || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试");
        return;
      }

      setStatus("success");
      setFormError("");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络后重试");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-100">
      <SiteHeader />

      <main className="mx-auto w-full max-w-[1200px] flex-1 px-4 py-6 md:px-6">
        <Breadcrumb items={[{ label: "关于我们" }]} />

        <section className="mt-4 overflow-hidden rounded-xl bg-primary-800 px-6 py-10 text-center md:px-10 md:py-14">
          <h1 className="font-heading text-2xl font-bold text-background-50 md:text-4xl">关于飘花电影网</h1>
          <p className="mx-auto mt-4 max-w-3xl text-sm leading-relaxed text-background-200/85 md:text-base">
            飘花电影网是一个面向广大影视爱好者的内容门户，专注提供最新电影与电视剧的分类浏览、详细介绍以及影视资讯。
            我们希望通过清晰的分类与便捷的检索，让每一位影迷都能轻松发现感兴趣的作品，并及时了解行业动态。
          </p>
        </section>

        <section className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          {STATS.map((stat) => (
            <div key={stat.label} className="rounded-xl border border-background-200 bg-background-50 p-4 text-center md:p-5">
              <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                <i className={`${stat.icon} text-xl`} />
              </span>
              <p className="mt-3 font-heading text-xl font-bold text-primary-700 md:text-2xl">{stat.value}</p>
              <p className="mt-1 text-sm text-foreground-500">{stat.label}</p>
            </div>
          ))}
        </section>

        <section className="mt-8">
          <h2 className="mb-4 flex items-center gap-2 border-b-2 border-background-200 pb-2.5 font-heading text-lg font-bold text-foreground-900">
            <span className="h-5 w-1 rounded-full bg-primary-600" />
            我们的服务
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {SERVICES.map((service) => (
              <div key={service.title} className="rounded-xl border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className={`${service.icon} text-xl`} />
                </span>
                <h3 className="mt-3 font-heading text-base font-semibold text-foreground-900">{service.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-500">{service.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent-500 text-primary-950">
                  <i className="ri-contacts-line text-sm" />
                </span>
                联系方式
              </h2>
              <ul className="mt-4 space-y-3 text-sm text-foreground-600">
                <li className="flex items-start gap-3">
                  <i className="ri-mail-line mt-0.5 text-primary-600" />
                  <span>service@piaohua-demo.com</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-phone-line mt-0.5 text-primary-600" />
                  <span>400-000-0000（工作日 9:00-18:00）</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-map-pin-line mt-0.5 text-primary-600" />
                  <span>北京市朝阳区影视产业园 A 座</span>
                </li>
              </ul>
              <p className="mt-4 rounded-md bg-secondary-50 px-3 py-2 text-xs leading-relaxed text-secondary-800">
                本站为影视内容演示站点，联系方式与表单信息仅供展示与体验。
              </p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-600 text-background-50">
                  <i className="ri-chat-1-line text-sm" />
                </span>
                意见反馈
              </h2>
              <p className="mt-2 text-sm text-foreground-500">有任何建议或合作意向，欢迎留言告诉我们。</p>

              {status === "success" ? (
                <div className="mt-5 flex flex-col items-center rounded-lg bg-secondary-50 px-4 py-8 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                    <i className="ri-check-line text-3xl" />
                  </span>
                  <p className="mt-4 font-medium text-foreground-800">提交成功，感谢您的反馈！</p>
                  <p className="mt-1 text-sm text-foreground-500">我们会尽快查看并与您联系。</p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400"
                  >
                    再填一份
                  </button>
                </div>
              ) : (
                <form id="ph-contact-form" data-readdy-form="ph-contact-form" onSubmit={handleSubmit} action={FORM_URL} method="post" className="mt-5 space-y-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label htmlFor="ph-name" className="mb-1.5 block text-sm font-medium text-foreground-700">
                        您的称呼
                      </label>
                      <input
                        id="ph-name"
                        name="name"
                        type="text"
                        required
                        placeholder="请输入称呼"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>
                    <div>
                      <label htmlFor="ph-email" className="mb-1.5 block text-sm font-medium text-foreground-700">
                        电子邮箱
                      </label>
                      <input
                        id="ph-email"
                        name="email"
                        type="email"
                        required
                        placeholder="请输入邮箱"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="ph-subject" className="mb-1.5 block text-sm font-medium text-foreground-700">
                      反馈类型
                    </label>
                    <select
                      id="ph-subject"
                      name="subject"
                      defaultValue="内容建议"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                    >
                      <option value="内容建议">内容建议</option>
                      <option value="资源反馈">资源反馈</option>
                      <option value="合作洽谈">合作洽谈</option>
                      <option value="其他问题">其他问题</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="ph-message" className="mb-1.5 block text-sm font-medium text-foreground-700">
                      留言内容
                    </label>
                    <textarea
                      id="ph-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      placeholder="请输入您的意见或建议（最多 500 字）"
                      className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>

                  <input
                    className="form-honey-field"
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === "error" && formError && (
                    <p className="flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2 text-sm text-accent-900">
                      <i className="ri-error-warning-line" />
                      {formError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        提交中...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line" />
                        提交反馈
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}